'use client';

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from './useToast';
import { withRetry } from '@shared/utils';
import { Logger } from '@shared/logger';
import type { UseDataManagementOptions } from '../types/UseDataManagementOptions';

export function useDataManagement<TData>({
  endpoint,
  queryKey,
  onSuccess,
  transformResponse = (data) => data,
}: UseDataManagementOptions<TData>) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TData | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const logger = new Logger();

  const { data = [], isLoading } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`Failed to fetch ${queryKey}`);
      const data = await response.json();
      return transformResponse(data);
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<TData>) => {
      const result = await withRetry(async () => {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        logger.debug(`Create response: ${response.status}`, 'data.users');
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message || 
            `Failed to create ${queryKey}. Status: ${response.status}. Data: ${JSON.stringify(data)}`
          );
        }
        
        return response.json();
      });
      
      return result.match({
        ok: (data) => data,
        err: (error) => {
          throw new Error(error.errorMessage);
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setIsOpen(false);
      toast({
        title: 'Success',
        description: 'Item created successfully',
      });
      onSuccess?.();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<TData> & { id: string }) => {
      const result = await withRetry(async () => {
        const response = await fetch(`${endpoint}/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message || 
            `Failed to update ${queryKey}. Status: ${response.status}`
          );
        }
        
        return response.json();
      }, {
        maxAttempts: 3,
        initialDelay: 1000,
      });
      
      return result.match({
        ok: (data) => data,
        err: (error) => {
          throw new Error(error.errorMessage);
        }
      });
    },
    onError: (error) => {
      logger.error('Update mutation error after retries', 'data.users', undefined, error);
      toast({
        title: 'Error',
        description: 'Failed to update. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setIsOpen(false);
      toast({
        title: 'Success',
        description: 'Item updated successfully',
      });
      onSuccess?.();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await withRetry(async () => {
        const response = await fetch(`${endpoint}/${id}`, {
          method: 'DELETE',
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message || 
            `Failed to delete ${queryKey}. Status: ${response.status}`
          );
        }
        
        return response.json();
      });
      
      return result.match({
        ok: (data) => data,
        err: (error) => {
          throw new Error(error.errorMessage);
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({
        title: 'Success',
        description: 'Item deleted successfully',
      });
      onSuccess?.();
    },
  });

  const handleSubmit = async (data: Partial<TData>) => {
    if (selectedItem) {
      await updateMutation.mutateAsync({ ...data, id: (selectedItem as any).id });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      // Error handling is already done in the mutation
      logger.error('Delete failed', 'data.users', undefined, error as Error);
    }
  };

  return {
    data,
    isLoading,
    isOpen,
    setIsOpen,
    selectedItem,
    setSelectedItem,
    handleSubmit,
    handleDelete,
    isDeleting: deleteMutation.isPending,
  };
} 